import '../i18n'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStreamEvent, useStreamReconnect } from '@/shared/realtime'
// Direct paths, not the barrel: the bell is mounted in the eager app layout, so
// a barrel import would drag the whole kit into the initial download.
import { AnimatedIcon } from '@/shared/ui/animated-icon'
import { Button } from '@/shared/ui/button'
import { CountBadge } from '@/shared/ui/count-badge'
import { BellIcon } from '@/shared/ui/icons/bell'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Spinner } from '@/shared/ui/spinner'
import { TOPBAR_PILL_TILE } from '@/shared/ui/topbar-pill'

import { useMarkAllRead, useMarkRead } from '../api/mutations'
import { notificationsBellQuery, unreadCountQuery } from '../api/queries'
import { notificationKeys } from '../api/query-keys'

import { NotificationItem } from './notification-item'

/**
 * The topbar bell — a tile inside the topbar pill, not a second piece of
 * chrome beside it. It renders as the pill's `leading` slot, so the layout
 * passes it in rather than placing it itself.
 *
 * It owns the realtime wiring for the whole feature: the stream carries only a
 * `notifications.new` signal, so every event and every reconnect simply
 * invalidates the notifications tree and lets the queries refetch. It is
 * always mounted for a signed-in user.
 */
export function NotificationsBell() {
  const { t } = useTranslation('notifications')
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all })

  useStreamEvent('notifications.new', invalidate)
  useStreamReconnect(invalidate)

  const unread = useQuery(unreadCountQuery())
  const bell = useQuery({ ...notificationsBellQuery(), enabled: isOpen })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const unreadCount = unread.data ?? 0

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <button type="button" title={t('bell')} aria-label={t('bell')} className={TOPBAR_PILL_TILE}>
            <AnimatedIcon icon={BellIcon} />
            {unreadCount > 0 && (
              // Inside the tile, not hanging off it: the capsule clips nothing,
              // but a badge past its edge would break the pill's silhouette.
              <CountBadge
                tone="danger"
                className="absolute top-0.5 right-0.5 size-4 min-w-4 px-1"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </CountBadge>
            )}
          </button>
        }
      />
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border-hairline px-4 py-3">
          <span className="text-h3 font-medium">{t('title')}</span>
          {unreadCount > 0 && (
            <Button
              size="xs"
              variant="ghost"
              isLoading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              {t('markAll')}
            </Button>
          )}
        </div>

        {/* The gutter is reserved because this list swaps a spinner for an
            unknown number of rows: a scrollbar arriving with them would narrow
            every row at the moment they appear. */}
        <div className="max-h-96 overflow-y-auto p-1.5 [scrollbar-gutter:stable]">
          {bell.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (bell.data ?? []).length === 0 ? (
            <p className="px-3 py-8 text-center text-caption text-muted-foreground">
              {t('empty.title')}
            </p>
          ) : (
            (bell.data ?? []).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRead={() => markRead.mutate(notification.id)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
